<template>
  <div id="app">
    <ckeditor :editor="editor"
              @change="onBlurClick"
              :value="content"
              @input="changeContent"
              :config="editorConfig"></ckeditor>
  </div>
</template>

<script>
import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor";
import EssentialsPlugin from "@ckeditor/ckeditor5-essentials/src/essentials";
import Alignment from "@ckeditor/ckeditor5-alignment/src/alignment";
import FontSize from "@ckeditor/ckeditor5-font/src/fontsize";
import FontFamily from "@ckeditor/ckeditor5-font/src/fontfamily";
import FontColor from "@ckeditor/ckeditor5-font/src/fontcolor";
import FontBackgroundColor from "@ckeditor/ckeditor5-font/src/fontbackgroundcolor";
import UploadAdapter from "@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter";
import Autoformat from "@ckeditor/ckeditor5-autoformat/src/autoformat";
import BoldPlugin from "@ckeditor/ckeditor5-basic-styles/src/bold";
import UnderLine from "@ckeditor/ckeditor5-basic-styles/src/underline";
import CodeBlock from "@ckeditor/ckeditor5-code-block/src/codeblock";
import ItalicPlugin from "@ckeditor/ckeditor5-basic-styles/src/italic";
import Strikethrough from "@ckeditor/ckeditor5-basic-styles/src/strikethrough";
import Heading from "@ckeditor/ckeditor5-heading/src/heading";
import CKFinder from "@ckeditor/ckeditor5-ckfinder/src/ckfinder";
import List from "@ckeditor/ckeditor5-list/src/list";
import SimpleUploadAdapter from "@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter";
import Image from "@ckeditor/ckeditor5-image/src/image.js";
import ImageCaption from "@ckeditor/ckeditor5-image/src/imagecaption.js";
import ImageResize from "@ckeditor/ckeditor5-image/src/imageresize.js";
import ImageStyle from "@ckeditor/ckeditor5-image/src/imagestyle.js";
import ImageToolbar from "@ckeditor/ckeditor5-image/src/imagetoolbar.js";
import ImageUpload from "@ckeditor/ckeditor5-image/src/imageupload.js";
import LinkPlugin from "@ckeditor/ckeditor5-link/src/link";
import Table from "@ckeditor/ckeditor5-table/src/table";
import TableToolbar from "@ckeditor/ckeditor5-table/src/tabletoolbar";
import Indent from "@ckeditor/ckeditor5-indent/src/indent";
import IndentBlock from "@ckeditor/ckeditor5-indent/src/indentblock";
import MediaEmbed from "@ckeditor/ckeditor5-media-embed/src/mediaembed";
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph";
import PasteFromOffice from "@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice";
import TextTransformation from "@ckeditor/ckeditor5-typing/src/texttransformation";
import ParagraphPlugin from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import SourceEditing from '@ckeditor/ckeditor5-source-editing/src/sourceediting';
import GeneralHtmlSupport from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport';

import JwtService from "@/jwt.service";

const transformations = {
  extra: [
    { from: '-', to: '–' }
  ]
};

export default {
  name: 'app',
  props: {
    content: String
  },
  data() {  
    return {
      editor: ClassicEditor,
      editorData: '<p>Content of the editor.</p>',
      editorConfig: {
        allowedContent: true,
        autoParagraph: false,
        fillEmptyBlocks: false,
        typing: {
          transformations: transformations
        },
        placeholder:'Nhập mô tả',
        // The configuration of the editor.
        plugins: [
          EssentialsPlugin,
          Alignment,
          FontSize,
          FontFamily,
          FontColor,
          FontBackgroundColor,
          UploadAdapter,
          Autoformat,
          BoldPlugin,
          ItalicPlugin,
          Strikethrough,
          UnderLine,
          Heading,
          List,
          CodeBlock,
          CKFinder,
          SimpleUploadAdapter,
          Image,
          ImageCaption,
          ImageResize,
          ImageStyle,
          ImageToolbar,
          ImageUpload,
          Indent,
          IndentBlock,
          LinkPlugin,
          Table,
          TableToolbar,
          MediaEmbed,
          Paragraph,
          PasteFromOffice,
          TextTransformation,
          ParagraphPlugin,
          SourceEditing,
          GeneralHtmlSupport
        ],

        toolbar: {
            items: [
                "heading",
                "|",
                "fontfamily",
                "fontsize",
                "fontColor",
                "fontBackgroundColor",
                "|",
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "|",
                "alignment",
                "|",
                "numberedList",
                "bulletedList",
                "|",
                "indent",
                "outdent",
                "|",
                "link",
                "codeBlock",
                "imageUpload",
                "insertTable",
                "mediaEmbed",
                "|",
                "undo",
                "redo",
                "|",
                "ckfinder",
                'sourceEditing'
            ]
        },

        image: {
          styles: ["full", "alignLeft", "alignCenter", "alignRight"],
          toolbar: [
            "imageStyle:alignLeft",
            "imageStyle:alignCenter",
            "imageStyle:alignRight",
            "imageTextAlternative",
            "imageStyle:full",
            "imageRes ize"
          ]
        },

        table: {
          contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"]
        },
        link: {
          decorators: {
            openInNewTab: {
              mode: 'manual',
              label: 'Open in a new tab',
              attributes: {
                target: '_blank',
                rel: 'noopener noreferrer'
              }
            }
          }
        },
        heading: {
          options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
            { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
            { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
          ]
        },
        
        alignment: {
          options: ["left", "right", "center"]
        },htmlSupport: {
          allow: [
            {
              name: /.*/,
              attributes: true,
              // classes: true,
              styles: true,
            },
          ],
        },

        simpleUpload: {
          // The URL that the images are uploaded to.
          uploadUrl: process.env.VUE_APP_API_URL + "/admin/ck-upload",
          // Headers sent along with the XMLHttpRequest to the upload server.
          headers: {
            Authorization: "Bearer " + JwtService.getToken()
          }
        },

        ckfinder: {
          // Open the file manager in the pop-up window.
          uploadUrl: process.env.VUE_APP_API_URL + "/admin/ck-upload",
          options: {
            resourceType: "Image"
          }
        }

      },
    };
  },
  methods:{
    changeContent(value) {
      this.$emit("getData", value);
    },

    onBlurClick(value) {
      this.$emit("getSeo", value);
    }
  }
}
</script>
<style>
  .ck-editor__editable {
    min-height: 600px;
   }
</style>
